import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { FilterState } from './SearchFilters';
import { format } from 'date-fns';

interface SaveSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  filterState: FilterState;
  onSave?: () => void;
}

export const SaveSearchModal: React.FC<SaveSearchModalProps> = ({ 
  isOpen, 
  onClose, 
  filterState,
  onSave 
}) => {
  const [searchName, setSearchName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!searchName.trim()) {
      toast.error('Please enter a name for this search');
      return;
    }

    setIsSaving(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error('You must be logged in to save searches');
        setIsSaving(false);
        return;
      }

      const { error } = await supabase
        .from('search_presets')
        .insert({
          user_id: user.id,
          name: searchName.trim(),
          origin: filterState.origin,
          destination: filterState.destination,
          dho_radius: filterState.dhoRadius,
          dhd_radius: filterState.dhdRadius,
          equipment_types: filterState.selectedEquipment,
          load_type: filterState.loadType,
          date_range: filterState.dateRange.map(d => format(d, 'yyyy-MM-dd')),
          hide_canada: filterState.hideCanada,
          search_back: filterState.searchBack
        });

      if (error) throw error;

      toast.success('Search saved!');
      setSearchName('');
      onSave?.(); // Trigger refresh of saved searches list
      onClose();
    } catch (error) {
      console.error('Error saving search:', error);
      toast.error('Failed to save search');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Save Search</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="search-name">Name this search</Label>
            <Input
              id="search-name"
              placeholder="e.g., LA to Chicago Van"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSave();
                }
              }}
              autoFocus
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
